import { supabase } from "./supabase";
import { getProfile } from "./profiles";
import { logger } from "./logger";

// ─── Types ───────────────────────────────────────────────────────────────────

export type EntiteType = "facture_achat" | "facture_vente" | "facture_vente_import" | "transaction" | "ecriture";

export interface Commentaire {
  id: string;
  dossier_id: string;
  entite_type: EntiteType;
  entite_id: string;
  message: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  // Infos user (jointes)
  user_email?: string;
  user_nom?: string;
  user_prenom?: string;
  user_role?: string;
  /** URL publique avatar (profiles), renseignée après enrichissement RPC */
  avatar_url?: string;
}

// ─── Fetch ───────────────────────────────────────────────────────────────────

export async function fetchCommentaires(
  entiteType: EntiteType,
  entiteId: string,
  dossierId?: string
): Promise<Commentaire[]> {
  let query = supabase
    .from("commentaires")
    .select("*")
    .eq("entite_type", entiteType)
    .eq("entite_id", entiteId)
    .is("deleted_at", null);
  if (dossierId?.trim()) query = query.eq("dossier_id", dossierId.trim());
  const { data, error } = await query.order("created_at", { ascending: true });

  if (error) throw error;

  const rows = data || [];
  const userIds = [...new Set(rows.map((c) => c.user_id))];
  const authorById = new Map<
    string,
    { email: string; prenom: string | null; nom: string | null; avatar_url: string | null }
  >();

  if (userIds.length > 0) {
    const { data: authors, error: authorsErr } = await supabase.rpc("get_commentaire_author_info", {
      p_user_ids: userIds,
    });
    if (authorsErr) {
      logger.error("[fetchCommentaires] get_commentaire_author_info:", authorsErr.message, authorsErr);
    }
    if (!authorsErr && authors) {
      for (const a of authors as {
        user_id: string;
        email: string;
        prenom: string | null;
        nom: string | null;
        avatar_url: string | null;
      }[]) {
        authorById.set(a.user_id, {
          email: a.email ?? "",
          prenom: a.prenom,
          nom: a.nom,
          avatar_url: a.avatar_url,
        });
      }
    }
  }

  // Complément : lecture RLS OK sur sa propre ligne profil si la RPC distante n’a pas encore avatar_url (ex. migration 074 non appliquée).
  const {
    data: { user: authUser },
  } = await supabase.auth.getUser();
  if (authUser && userIds.includes(authUser.id)) {
    try {
      const mine = await getProfile(authUser.id);
      const myAvatar = mine?.avatar_url?.trim();
      if (myAvatar) {
        const prev = authorById.get(authUser.id);
        authorById.set(authUser.id, {
          email: prev?.email ?? "",
          prenom: prev?.prenom ?? null,
          nom: prev?.nom ?? null,
          avatar_url: prev?.avatar_url?.trim() || myAvatar,
        });
      }
    } catch {
      /* ignore */
    }
  }

  return rows.map((c) => {
    const a = authorById.get(c.user_id);
    const url = a?.avatar_url?.trim() || undefined;
    return {
      ...c,
      user_email: a?.email?.trim() || undefined,
      user_prenom: a?.prenom?.trim() || undefined,
      user_nom: a?.nom?.trim() || undefined,
      avatar_url: url && url.length > 0 ? url : undefined,
    };
  });
}

export async function countCommentaires(
  entiteType: EntiteType,
  entiteId: string,
  dossierId?: string
): Promise<number> {
  let query = supabase
    .from("commentaires")
    .select("*", { count: "exact", head: true })
    .eq("entite_type", entiteType)
    .eq("entite_id", entiteId)
    .is("deleted_at", null);
  if (dossierId?.trim()) query = query.eq("dossier_id", dossierId.trim());
  const { count, error } = await query;

  if (error) throw error;
  return count || 0;
}

// ─── Actions ─────────────────────────────────────────────────────────────────

export async function ajouterCommentaire(
  dossierId: string,
  entiteType: EntiteType,
  entiteId: string,
  message: string
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Non authentifié");

  const payload = {
    dossier_id: dossierId,
    entite_type: entiteType,
    entite_id: entiteId,
    message: message.trim(),
    user_id: user.id,
  };
  console.log("[commentaires] INSERT payload", payload);

  const { error } = await supabase
    .from("commentaires")
    .insert(payload);

  if (error) {
    console.error("[commentaires] INSERT error", {
      message: error.message,
      code: error.code,
      details: error.details,
      hint: error.hint,
      payload,
    });
    throw error;
  }
}

export async function modifierCommentaire(
  commentaireId: string,
  message: string
): Promise<void> {
  const { error } = await supabase
    .from("commentaires")
    .update({ message: message.trim(), updated_at: new Date().toISOString() })
    .eq("id", commentaireId);

  if (error) throw error;
}

export async function supprimerCommentaire(commentaireId: string): Promise<void> {
  // Soft delete
  const { error } = await supabase
    .from("commentaires")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", commentaireId);

  if (error) throw error;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function initialeDepuisEmail(email: string): string {
  const local = email.split("@")[0]?.trim() || "";
  const c = local.charAt(0);
  return c ? c.toUpperCase() : "M";
}

export function getInitiales(commentaire: Commentaire): string {
  const p = commentaire.user_prenom?.trim();
  const n = commentaire.user_nom?.trim();
  if (p && n) return `${p[0]}${n[0]}`.toUpperCase();
  if (p && p.length >= 2) return p.slice(0, 2).toUpperCase();
  if (p) return p[0].toUpperCase();
  if (n && n.length >= 2) return n.slice(0, 2).toUpperCase();
  if (n) return n[0].toUpperCase();
  if (commentaire.user_email?.trim()) return initialeDepuisEmail(commentaire.user_email.trim());
  return "M";
}

export function getNomAffiche(commentaire: Commentaire): string {
  const p = commentaire.user_prenom?.trim();
  const n = commentaire.user_nom?.trim();
  if (p && n) return `${p} ${n}`;
  if (p) return p;
  if (n) return n;
  if (commentaire.user_email?.trim()) return commentaire.user_email.trim();
  return "Collaborateur";
}

export function formatDateRelative(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;

  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}
